(() => {
  const script = document.currentScript;
  const shopUrl = script?.dataset.shopUrl || window.location.origin;
  const endpoint = `${shopUrl.replace(/\/$/, '')}/apps/appio/review/get-products-ratings`;
  const cardSelector = '[data-yx-product-card][data-yx-product-id]';
  const retryDelays = [1500, 5000];
  const cache = new Map();
  const pending = new Set();
  const retryState = new Map();
  let scheduled = false;
  let retryTimer = 0;

  /* The five-star mask contains visible gaps between stars. A plain
   * rating / 5 percentage therefore cuts a fractional final star too early
   * (4.5 looked closer to 4.3). Convert the numeric value to the matching
   * visible point inside the mask so half ratings render as half stars. */
  const getStarMaskFill = (rating) => {
    const normalizedRating = Math.min(5, Math.max(0, rating));
    if (normalizedRating <= 0) return '0%';
    if (normalizedRating >= 5) return '100%';

    const maskWidth = 78.5535;
    const starStride = 16.7107;
    const starInset = 0.25971;
    const starWidth = 11.19129;
    const fullStars = Math.floor(normalizedRating);
    const fraction = normalizedRating - fullStars;
    const fillPoint = fraction === 0
      ? fullStars * starStride
      : fullStars * starStride + starInset + fraction * starWidth;

    return `${Math.round((fillPoint / maskWidth) * 10000) / 100}%`;
  };

  const scheduleNextRetry = () => {
    window.clearTimeout(retryTimer);
    retryTimer = 0;

    const nextAttemptAt = Math.min(
      ...Array.from(retryState.values(), (state) => state.nextAttemptAt)
    );
    if (!Number.isFinite(nextAttemptAt)) return;

    retryTimer = window.setTimeout(() => {
      retryTimer = 0;
      scheduleHydration();
    }, Math.max(0, nextAttemptAt - Date.now()));
  };

  const renderRating = (card, ratingData) => {
    const ratingElement = card.querySelector('[data-yx-rating]');
    if (!ratingElement) return;

    const rating = Number.parseFloat(ratingData?.rating);
    const count = Number.parseInt(ratingData?.raters, 10);
    if (!Number.isFinite(rating) || rating <= 0 || !Number.isFinite(count) || count <= 0) {
      if (!ratingElement.hidden) ratingElement.hidden = true;
      if (ratingElement.hasAttribute('aria-label')) ratingElement.removeAttribute('aria-label');
      if (card.dataset.yxRatingState !== 'empty') card.dataset.yxRatingState = 'empty';
      return;
    }

    const normalizedRating = Math.min(5, Math.max(0, rating));
    const fill = getStarMaskFill(normalizedRating);
    const reviewLabel = count === 1 ? 'review' : 'reviews';

    if (ratingElement.style.getPropertyValue('--yx-rating-fill') !== fill) {
      ratingElement.style.setProperty('--yx-rating-fill', fill);
    }
    if (ratingElement.dataset.yxRatingValue !== String(normalizedRating)) ratingElement.dataset.yxRatingValue = String(normalizedRating);
    if (ratingElement.dataset.yxRatingCount !== String(count)) ratingElement.dataset.yxRatingCount = String(count);
    const label = document.documentElement.lang === 'es'
      ? `Valoración de ${normalizedRating} sobre 5, basada en ${count} ${count === 1 ? 'reseña' : 'reseñas'}`
      : `Rating ${normalizedRating} out of 5, based on ${count} ${reviewLabel}`;
    if (ratingElement.getAttribute('aria-label') !== label) ratingElement.setAttribute('aria-label', label);
    if (ratingElement.hidden) ratingElement.hidden = false;
    if (card.dataset.yxRatingState !== 'ready') card.dataset.yxRatingState = 'ready';
  };

  const hydrateRatings = async () => {
    scheduled = false;
    const cards = Array.from(document.querySelectorAll(cardSelector));
    const cardsByProductId = new Map();

    cards.forEach((card) => {
      const productId = card.dataset.yxProductId;
      if (!productId) return;
      if (!cardsByProductId.has(productId)) cardsByProductId.set(productId, []);
      cardsByProductId.get(productId).push(card);
    });

    cardsByProductId.forEach((matchingCards, productId) => {
      if (!cache.has(productId)) return;
      matchingCards.forEach((card) => renderRating(card, cache.get(productId)));
    });

    const now = Date.now();
    const productIds = Array.from(cardsByProductId.keys()).filter((productId) => {
      const retry = retryState.get(productId);
      return !cache.has(productId)
        && !pending.has(productId)
        && (!retry || retry.nextAttemptAt <= now);
    });
    if (!productIds.length) return;

    productIds.forEach((productId) => pending.add(productId));

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productIds,
          locale: document.documentElement.lang || 'en',
          useImportSummary: false,
          saveRatings: false,
        }),
      });
      if (!response.ok) throw new Error(`Appio ratings request failed with HTTP ${response.status}`);

      const ratings = await response.json();
      const ratingsByProductId = new Map(
        Object.values(ratings || {}).map((ratingData) => [String(ratingData.productId), ratingData])
      );

      productIds.forEach((productId) => {
        const ratingData = ratingsByProductId.get(productId) || null;
        retryState.delete(productId);
        cache.set(productId, ratingData);
        cardsByProductId.get(productId)?.forEach((card) => renderRating(card, ratingData));
      });
      scheduleNextRetry();
      scheduleHydration();
    } catch (error) {
      console.warn('[YURINOX] Product ratings could not be loaded.', error);
      productIds.forEach((productId) => {
        const attempts = (retryState.get(productId)?.attempts || 0) + 1;
        const delay = retryDelays[attempts - 1];
        retryState.set(productId, {
          attempts,
          nextAttemptAt: delay === undefined ? Number.POSITIVE_INFINITY : Date.now() + delay,
        });
        cardsByProductId.get(productId)?.forEach((card) => {
          card.dataset.yxRatingState = 'error';
        });
      });
      scheduleNextRetry();
    } finally {
      productIds.forEach((productId) => pending.delete(productId));
    }
  };

  const scheduleHydration = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(hydrateRatings);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleHydration, { once: true });
  } else {
    scheduleHydration();
  }

  const observer = new MutationObserver((mutations) => {
    if (mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) =>
      node instanceof Element && (
        node.matches(cardSelector) || node.querySelector(cardSelector) ||
        ((node.matches('[data-yx-rating]') || node.querySelector('[data-yx-rating]')) && node.closest(cardSelector))
      )
    ))) {
      scheduleHydration();
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
